// Account Sessions (admin only): who logged in to the CRM, on which device, and when.
// Data: GET /auth/sessions (Models/UserSession.js). All times in US Pacific.
import { useCallback, useEffect, useState } from 'react';
import { Button, Card, DatePicker, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import {
  DesktopOutlined,
  GoogleOutlined,
  KeyOutlined,
  MobileOutlined,
  ReloadOutlined,
  TabletOutlined,
} from '@ant-design/icons';
import API from '../../API/API';
import { formatPT, zoneAbbrPT, toApiDays, disabledFuturePT } from '../../utils/usTime';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_TAGS = {
  active: { color: 'green', label: 'Active' },
  'logged-out': { color: 'default', label: 'Logged out' },
  'idle-logout': { color: 'orange', label: 'Logged out (idle)' },
  expired: { color: 'default', label: 'Expired' },
};

const DEVICE_ICONS = {
  mobile: <MobileOutlined />,
  tablet: <TabletOutlined />,
  desktop: <DesktopOutlined />,
};

// '5 min ago', '3 hr ago', '2 days ago'
const timeAgo = (value) => {
  const ms = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(ms)) return '';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const ptDateTime = (value) => (value ? `${formatPT(value, 'MMM DD, YYYY hh:mm A')} ${zoneAbbrPT(new Date(value))}` : '—');

export default function AccountSessions() {
  const [status, setStatus] = useState('active');
  const [userId, setUserId] = useState(undefined);
  const [range, setRange] = useState(null);
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  // Users for the filter
  useEffect(() => {
    (async () => {
      const res = await API.userGetAll();
      if (res?.success && Array.isArray(res.data)) {
        setUsers(
          res.data
            .filter((u) => !u.isDeleted)
            .map((u) => ({
              value: u._id,
              label: `${u.userName || ''} — ${[u.firstName, u.lastName].filter(Boolean).join(' ')} (${u.role})`,
            }))
        );
      }
    })();
  }, []);

  const loadSessions = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const [startDate, endDate] = toApiDays(range);
      const res = await API.getSessions({
        status,
        page,
        limit: pageSize,
        ...(userId ? { userId } : {}),
        ...(startDate ? { startDate, endDate } : {}),
      });
      if (res?.success) {
        setRows(res.data || []);
        setPagination({ current: res.pagination.page, pageSize: res.pagination.limit, total: res.pagination.total });
      } else {
        message.error(res?.message || 'Failed to load sessions');
      }
    } catch (err) {
      console.error('loadSessions error', err);
      message.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [status, userId, range]);

  useEffect(() => {
    loadSessions(1, pagination.pageSize);
    // Reload from page 1 whenever a filter changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, userId, range]);

  const columns = [
    {
      title: 'User',
      key: 'user',
      width: 240,
      render: (_, r) => (
        <div>
          <Space size={6}>
            <Text strong>{r.user?.userName || 'Deleted user'}</Text>
            {r.isCurrent && <Tag color="blue" style={{ marginInlineEnd: 0 }}>Current</Tag>}
          </Space>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {[r.user?.firstName, r.user?.lastName].filter(Boolean).join(' ')}
          </div>
          {r.user?.role && (
            <Tag style={{ marginTop: 4, fontSize: 11 }}>
              {r.user.role}{r.user.subRole ? ` | ${r.user.subRole}` : ''}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Device',
      key: 'device',
      width: 200,
      render: (_, r) => (
        <Space>
          <span style={{ fontSize: 18, color: '#4b5563' }}>{DEVICE_ICONS[r.device?.deviceType] || DEVICE_ICONS.desktop}</span>
          <Text>{`${r.device?.browser || 'Other'} (${r.device?.os || 'Other'})`}</Text>
        </Space>
      ),
    },
    {
      title: 'IP Address',
      dataIndex: 'ip',
      key: 'ip',
      width: 150,
      render: (ip) => <Text copyable={Boolean(ip)}>{ip || '—'}</Text>,
    },
    {
      title: 'Login',
      dataIndex: 'loginMethod',
      key: 'loginMethod',
      width: 110,
      render: (m) => (m === 'google'
        ? <Tag icon={<GoogleOutlined />} color="geekblue">Google</Tag>
        : <Tag icon={<KeyOutlined />}>Password</Tag>),
    },
    {
      title: 'Logged In',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (v) => ptDateTime(v),
    },
    {
      title: 'Last Active',
      dataIndex: 'lastActiveAt',
      key: 'lastActiveAt',
      width: 200,
      render: (v) => (
        <div>
          <div>{ptDateTime(v)}</div>
          {v && <div style={{ fontSize: 12, color: '#6b7280' }}>{timeAgo(v)}</div>}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 170,
      render: (_, r) => {
        const s = STATUS_TAGS[r.status] || STATUS_TAGS.active;
        return (
          <div>
            <Tag color={s.color}>{s.label}</Tag>
            {r.loggedOutAt && (
              <Tooltip title="Logged out at">
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{ptDateTime(r.loggedOutAt)}</div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card bordered={false} style={{ borderRadius: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>Account Sessions</Title>
            <Text type="secondary">Who logged in to the CRM, on which device (times in US Pacific)</Text>
          </div>
          <Space wrap>
            <Select
              value={status}
              onChange={setStatus}
              style={{ width: 150 }}
              options={[
                { value: 'active', label: 'Active sessions' },
                { value: 'all', label: 'All sessions' },
              ]}
            />
            <Select
              allowClear
              showSearch
              placeholder="All users"
              value={userId}
              onChange={setUserId}
              options={users}
              optionFilterProp="label"
              style={{ width: 280 }}
            />
            <RangePicker
              value={range}
              onChange={setRange}
              disabledDate={disabledFuturePT}
              format="MM/DD/YYYY"
              placeholder={['Login from', 'Login to']}
            />
            <Button icon={<ReloadOutlined />} onClick={() => loadSessions(pagination.current, pagination.pageSize)}>
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          rowKey="_id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100],
            showTotal: (total) => `${total} session${total === 1 ? '' : 's'}`,
          }}
          onChange={(p) => loadSessions(p.current, p.pageSize)}
        />
      </Card>
    </div>
  );
}
